import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Mail,
  User,
  Package,
  Loader2,
  CheckCircle2,
  X,
  AlertTriangle,
  FileText,
  Sparkles,
  // Send, // Used only by auto-reply (not yet implemented)
} from "lucide-react";
import { format } from "date-fns";
import { navigate } from "wouter/use-browser-location";
import { useRoute } from "wouter";
import { useGmailEmail, useRejectEmail, useClassifyEmails /*, useApproveAndAutoReply */ } from "@/hooks/useGmailEmails";
import type { InMemoryEmail, BuyerMatchCandidate, ExtractedData, ItemMatchCandidate } from "@/types/gmail";
import { useBuyers } from "@/hooks/useBuyers";
import { useItems } from "@/hooks/useItems";
import { matchBuyer, matchAllLineItems, type BuyerRecord, type ItemRecord, type LineItemMatchResult } from "@/utils/matchingEngine";
import { QuotationMatchingModal } from "@/components/QuotationMatchingModal";

const REVIEW_REASON_LABELS: Record<string, string> = {
  AUTO_REPLY_DISABLED: "Auto-reply is disabled in settings.",
  BELOW_MIN_PRICE: "One or more items are below the minimum selling price.",
  BELOW_MIN_QTY: "One or more items are below the minimum order quantity.",
  MANUAL_OVERRIDE: "Manual review required.",
  REPLY_TO_EXISTING_QUOTATION: "This is a reply to an existing quotation in this thread.",
};

export const EmailQuotationReview: React.FC = () => {
  const [, params] = useRoute("/email-quotation/:id");
  const emailId = params?.id;

  const { data: email, isLoading: emailLoading } = useGmailEmail(emailId);
  const { buyers, isLoading: buyersLoading } = useBuyers();
  const { items, isLoading: itemsLoading } = useItems();
  const rejectEmail = useRejectEmail();
  const classifyEmail = useClassifyEmails();
  // const approveAndAutoReply = useApproveAndAutoReply(); // Auto-reply not yet implemented

  const [showMatchingModal, setShowMatchingModal] = React.useState(false);
  const [buyerMatch, setBuyerMatch] = React.useState<{ result: ReturnType<typeof matchBuyer>; selected: BuyerMatchCandidate | null } | null>(null);
  const [itemMatches, setItemMatches] = React.useState<Array<{ result: LineItemMatchResult; selected: ItemMatchCandidate | null }>>([]);
  const [matchingReady, setMatchingReady] = React.useState(false);

  const isLoading = emailLoading || buyersLoading || itemsLoading;

  // Run matching when data loads
  React.useEffect(() => {
    if (!email?.extractedData || !buyers || !items) return;

    const extractedData = email.extractedData as ExtractedData;
    const rawBuyers = ((buyers?.data ?? buyers) as unknown as Array<{
      id: string;
      code: string | null;
      name: string;
      email: string;
      tin: string;
      registrationType: string;
      registrationNumber: string;
      buyerAddress?: { cityName?: string; state?: string; country?: string };
    }>) || [];
    const buyerList: BuyerRecord[] = rawBuyers.map((b) => ({
      id: b.id,
      buyerCode: b.code,
      buyerName: b.name,
      buyerEmail: b.email,
      buyerTin: b.tin,
      buyerRegistrationType: b.registrationType,
      buyerRegistrationNumber: b.registrationNumber,
      buyerCity: b.buyerAddress?.cityName || "",
      buyerState: b.buyerAddress?.state || "",
      buyerCountry: b.buyerAddress?.country || "",
    }));
    const itemList = (items?.data as unknown as ItemRecord[]) || [];

    const bMatch = matchBuyer(extractedData.buyer, buyerList);
    const lineItemMatches = matchAllLineItems(extractedData.line_items || [], itemList);

    setBuyerMatch({ result: bMatch, selected: bMatch.matches[0] || null });
    setItemMatches(lineItemMatches.map((m) => ({ result: m, selected: m.matchResult.matches[0] || null })));
    setMatchingReady(true);
  }, [email, buyers, items]);

  const handleClassify = () => {
    if (!email) return;
    // Build an InMemoryEmail from the loaded DB record so we can re-classify it
    const inMemoryEmail: InMemoryEmail = {
      messageId: email.gmailMessageId,
      threadId: email.gmailThreadId,
      subject: email.emailSubject,
      fromEmail: email.emailFrom,
      emailDate: email.emailDate,
      snippet: email.emailSnippet || "",
      rawText: email.rawText,
    };
    classifyEmail.mutate([inMemoryEmail]);
  };

  const handleCreateQuotation = () => {
    if (!matchingReady) return;
    setShowMatchingModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Email not found.
      </div>
    );
  }

  const extractedData = email.extractedData as ExtractedData | null;
  const canCreateQuotation =
    email.status === "PENDING_REVIEW" && email.isQuotationRequest === true;
  const needsClassification = email.status === "PENDING_CLASSIFICATION";

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/email-inbox")} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Inbox
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{email.emailSubject}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            From: {email.emailFrom} &middot;{" "}
            {format(new Date(email.emailDate), "dd MMM yyyy, HH:mm")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {email.isQuotationRequest === true && (
            <Badge variant="outline" className="text-green-600 border-green-300 gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Quotation Request
            </Badge>
          )}
          {email.isQuotationRequest === false && (
            <Badge variant="outline" className="text-orange-500 border-orange-300 gap-1">
              <X className="h-3 w-3" />
              Not a Quotation
            </Badge>
          )}
          {email.isQuotationRequest === null && (
            <Badge variant="secondary" className="gap-1">
              <Loader2 className="h-3 w-3" />
              Not Classified
            </Badge>
          )}
        </div>
      </div>

      {/* Review Required Warning */}
      {email.reviewRequiredReason && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800">Manual Review Required</p>
            <p className="text-amber-700">
              {REVIEW_REASON_LABELS[email.reviewRequiredReason] || email.reviewRequiredReason}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      {needsClassification && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">This email hasn't been classified yet.</p>
                <p className="text-xs text-muted-foreground">Run AI classification to extract quotation details.</p>
              </div>
              <Button onClick={handleClassify} disabled={classifyEmail.isPending} className="gap-2">
                {classifyEmail.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Classify with AI
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Email Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4" />
              Email Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md bg-muted p-3 text-sm whitespace-pre-wrap max-h-80 overflow-y-auto font-mono text-xs">
              {email.rawText}
            </div>
          </CardContent>
        </Card>

        {/* Extracted Data */}
        <div className="space-y-4">
          {/* Buyer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4" />
                Extracted Buyer Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {extractedData?.buyer ? (
                <>
                  {Object.entries(extractedData.buyer)
                    .filter(([, v]) => v)
                    .map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="text-muted-foreground w-32 shrink-0 capitalize">
                          {key.replace(/_/g, " ").replace("buyer ", "")}:
                        </span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  {matchingReady && buyerMatch && (
                    <div className="mt-3 pt-3 border-t">
                      {buyerMatch.result.type === "exact" ? (
                        <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                          Exact match found in database
                        </Badge>
                      ) : buyerMatch.result.type === "fuzzy" ? (
                        <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                          {buyerMatch.result.matches.length} possible match(es) found
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-500 border-orange-300 text-xs">
                          No buyer match — will create new
                        </Badge>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground text-xs">No buyer data extracted.</p>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4" />
                Extracted Line Items ({extractedData?.line_items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {extractedData?.line_items && extractedData.line_items.length > 0 ? (
                extractedData.line_items.map((item, idx) => {
                  const match = itemMatches[idx];
                  return (
                    <div key={idx} className="rounded-md border p-3 text-sm space-y-1">
                      <div className="font-medium">{item.item_desc || "—"}</div>
                      <div className="text-xs text-muted-foreground flex gap-4">
                        {item.qty && <span>Qty: {item.qty}</span>}
                        {item.unit_price_myr && <span>Price: MYR {item.unit_price_myr}</span>}
                        {item.product_code && <span>Code: {item.product_code}</span>}
                      </div>
                      {match && matchingReady && (
                        <div className="mt-1">
                          {match.result.matchResult.type === "exact" ? (
                            <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                              Exact item match
                            </Badge>
                          ) : match.result.matchResult.type === "fuzzy" ? (
                            <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                              {match.result.matchResult.matches.length} possible match(es)
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-500 border-orange-300 text-xs">
                              New item — will create
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-muted-foreground text-xs">No line items extracted.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action Buttons */}
      {(canCreateQuotation || email.status === "PENDING_REVIEW") && (
        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={handleCreateQuotation}
            disabled={!extractedData || !matchingReady}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            Create Quotation
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="gap-2 text-destructive hover:text-destructive">
                <X className="h-4 w-4" />
                Reject Email
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reject Email</AlertDialogTitle>
                <AlertDialogDescription>
                  Mark this email as rejected. It will no longer appear in your review queue.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => rejectEmail.mutate({ id: email.id })}
                  className="bg-destructive text-destructive-foreground"
                >
                  Reject
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {email.status === "CONVERTED" && email.quotationId && (
        <div className="rounded-md bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-900/50 p-3 flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">Quotation Created</p>
          </div>
          {/* Auto-reply not yet implemented
          <Button
            variant="outline"
            size="sm"
            onClick={() => approveAndAutoReply.mutate({ emailId: email.id, quotationId: email.quotationId! })}
            disabled={approveAndAutoReply.isPending}
            className="gap-2"
          >
            {approveAndAutoReply.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send Auto-Reply
          </Button>
          */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/quotation/view", { state: { id: email.quotationId } })}
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            View Quotation
          </Button>
        </div>
      )}

      {/* AUTO_REPLIED status — auto-reply not yet implemented
      {email.status === "AUTO_REPLIED" && (
        <div className="rounded-md bg-blue-50 border border-blue-200 dark:bg-blue-950/20 dark:border-blue-900/50 p-3 flex items-center gap-3">
          <CheckCircle2 className="h-4 w-4 text-blue-500" />
          <p className="text-sm font-medium text-blue-800">Auto-reply sent to customer.</p>
        </div>
      )}
      */}

      {/* Reply changes diff — shown when email is a reply to an existing quotation */}
      {extractedData?.changes && extractedData.changes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Requested Changes from Customer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {extractedData.change_summary && (
              <p className="text-sm text-muted-foreground italic">{extractedData.change_summary}</p>
            )}
            {extractedData.changes.map((change, idx) => (
              <div key={idx} className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm">
                <div className="font-medium text-amber-900">{change.item_desc}</div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="text-muted-foreground capitalize">{change.field}:</span>
                  <span className="line-through text-red-500">{String(change.old_value)}</span>
                  <span>→</span>
                  <span className="text-green-600 font-medium">{String(change.new_value)}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Matching Modal */}
      {showMatchingModal && extractedData && (
        <QuotationMatchingModal
          isOpen={showMatchingModal}
          onClose={() => setShowMatchingModal(false)}
          emailId={email.id}
          extractedData={extractedData}
          buyerMatchResult={buyerMatch?.result || { type: "none", confidence: 0, matches: [] }}
          itemMatchResults={itemMatches.map((m) => m.result)}
        />
      )}
    </div>
  );
};

export default EmailQuotationReview;
